// The sign-in editor's SSO providers (SCR-154): the list (button text, type, status, where it is
// read from) and the add/edit form, type first. A provider opens its summary with Edit, Move up,
// Move down and Remove, which asks Yes/No in place (the list's order is the sign-in page's). No
// client secret is ever entered, shown or echoed here: it is entered on the proxy (SCR-484), and
// the list says only whether the proxy keeps one. Providers are known by their button text; no id
// is shown.
import { useState } from "preact/hooks";
import { Checkbox, errorId, Field } from "../../primitives/field";
import { hasErrors, type FieldErrors } from "../../primitives/forms";
import type { HelpId } from "../../primitives/help";
import { Chip } from "../../primitives/page";
import { RowDetail } from "../../primitives/row-detail";
import { ROW_OPEN_CLASS, rowClickHandler } from "../../primitives/row-click";
import {
  blankProvider,
  kindOf,
  PROVIDER_TYPES,
  PROVIDERS_MAX,
  providerStatus,
  providerToDoc,
  providerUrl,
  typeLabel,
  validateProvider,
  type ProviderType,
  type ProviderValues,
} from "./providers";

const PROVIDERS_HELP: HelpId = "settings.sign_in.sso_providers";
const PROVIDER_TYPE_HELP: HelpId = "settings.sign_in.sso_providers.type";

/** Whether the proxy keeps the provider's client secret, in words; never the secret or where it is kept. */
export function secretState(provider: ProviderValues): string {
  return provider.secretName === null ? "not entered on the proxy yet" : "kept on the proxy";
}

type TextField = "label" | "metadataUrl" | "issuer" | "clientId" | "scopes" | "groupsAttribute" | "allowedEmailDomains" | "allowedGroups";

function ProviderForm({ initial, isNew, readOnly, onDone, onCancel }: { initial: ProviderValues; isNew: boolean; readOnly: boolean; onDone: (next: ProviderValues) => void; onCancel: () => void }) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const id = (field: string) => `provider-${field}`;
  const text = (field: TextField, label: string, placeholder?: string, rows?: number) => {
    const control = {
      id: id(field),
      class: "input-mono",
      placeholder,
      value: values[field],
      disabled: readOnly,
      "aria-invalid": errors[field] ? true : undefined,
      "aria-describedby": errors[field] ? errorId(id(field)) : undefined,
    };
    return (
      <Field id={id(field)} label={label} help={PROVIDERS_HELP} error={errors[field]}>
        {rows ? (
          <textarea {...control} rows={rows} onInput={(event) => setValues({ ...values, [field]: event.currentTarget.value })} />
        ) : (
          <input {...control} autocomplete="off" onInput={(event) => setValues({ ...values, [field]: event.currentTarget.value })} />
        )}
      </Field>
    );
  };
  const kind = kindOf(values.type);
  const done = () => {
    const found = validateProvider(values);
    setErrors(found);
    if (!hasErrors(found)) onDone(values);
  };

  return (
    <div class="card-note" role="group" aria-label={isNew ? "Add provider" : `Edit ${initial.label}`}>
      <Field id={id("type")} label="Type" help={PROVIDER_TYPE_HELP} hint={isNew ? "Choose the type first; it decides the fields." : "The type cannot change; remove the provider and add another."}>
        <select
          id={id("type")}
          value={values.type}
          disabled={!isNew || readOnly}
          onChange={(event) => setValues({ ...blankProvider(event.currentTarget.value as ProviderType), id: values.id })}
        >
          {PROVIDER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </Field>
      <div class="split-even">
        {text("label", "Button text", "Sign in with SSO")}
        {kind === "saml" ? (
          text("metadataUrl", "SAML metadata URL", "https://portal.sso.us-east-1.amazonaws.com/saml/metadata/…")
        ) : (
          <>
            {text("issuer", "Discovery URL (issuer)", "https://acme.okta.com")}
            {text("clientId", "Client id")}
          </>
        )}
        {kind === "oidc" && text("scopes", "Scopes", undefined, 3)}
        {text("groupsAttribute", "Groups attribute or claim", "groups")}
        {text("allowedEmailDomains", "Allowed email domains", "acme.com", 2)}
        {text("allowedGroups", "Allowed groups", undefined, 2)}
      </div>
      <Checkbox id={id("enabled")} label="Enabled" help={PROVIDERS_HELP} checked={values.enabled} disabled={readOnly} onChange={(checked) => setValues({ ...values, enabled: checked })} />
      <div class="row">
        <button type="button" class="button button-sm" disabled={readOnly} onClick={done}>
          {isNew ? "Add provider" : "Done"}
        </button>
        <button type="button" class="button button-ghost button-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export interface SsoProvidersProps {
  providers: readonly ProviderValues[];
  /** Shown as inherited (an installation that has not overridden the list). */
  inherited: boolean;
  /** Pinned by the deployment or otherwise not editable here. */
  readOnly: boolean;
  error?: string | undefined;
  onChange(next: ProviderValues[]): void;
}

export function SsoProviders({ providers, inherited, readOnly, error, onChange }: SsoProvidersProps) {
  // -1 adds, an index edits, null shows the list only.
  const [editing, setEditing] = useState<number | null>(null);
  // The provider whose summary is open, by its place in the list (it follows a move).
  const [selected, setSelected] = useState<number | null>(null);
  // The open summary asks "Remove?" in place of its actions; a draft edit needs no more than that.
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const select = (index: number | null) => {
    setConfirmingRemove(false);
    setSelected(index);
  };
  const replace = (index: number, next: ProviderValues) => onChange(providers.map((p, i) => (i === index ? next : p)));
  const move = (index: number, by: number) => {
    const target = index + by;
    if (target < 0 || target >= providers.length) return;
    const next = [...providers];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item!);
    onChange(next);
    select(target);
  };
  const remove = (index: number) => {
    select(null);
    onChange(providers.filter((_, i) => i !== index));
  };
  const edit = (index: number) => {
    select(null);
    setEditing(index);
  };
  const shown = selected === null ? null : (providers[selected] ?? null);

  return (
    <div class="settings-field">
      {inherited && <Chip tone="grey">inherited</Chip>}
      <p class="card-note">
        A client secret is never entered here. The customer's admin enters it on the proxy, and the provider works once it is there (see the proxy's Sign-in status).
      </p>
      {providers.length === 0 && editing === null && <p class="card-note">No providers: people sign in with the other methods below.</p>}
      <ul class="plain-list">
        {providers.map((p, index) => {
          const doc = providerToDoc(p);
          const status = providerStatus(doc);
          const summary = (
            <>
              <div class="row">
                {editing !== index && <button type="button" class={ROW_OPEN_CLASS} aria-label={`${p.label}, ${typeLabel(p.type)}`} />}
                <strong>{p.label}</strong> <span>{typeLabel(p.type)}</span>
                <Chip tone={status.tone}>{status.text}</Chip>
              </div>
              <div class="card-note">
                <code>{providerUrl(doc) || "no URL"}</code>
                {kindOf(p.type) === "oidc" && <> Client secret: {secretState(p)}</>}
              </div>
            </>
          );
          return editing === index ? (
            <li key={p.id || `new-${index}`}>
              {summary}
              <ProviderForm initial={p} isNew={false} readOnly={readOnly} onDone={(next) => (replace(index, next), setEditing(null))} onCancel={() => setEditing(null)} />
            </li>
          ) : (
            <li key={p.id || `new-${index}`} class="row-clickable" onClick={rowClickHandler(() => select(index))}>
              {summary}
            </li>
          );
        })}
      </ul>
      {shown && selected !== null && (
        <RowDetail
          title={shown.label}
          open
          onClose={() => select(null)}
          facts={[
            { label: "Type", value: typeLabel(shown.type) },
            { label: "Status", value: <Chip tone={providerStatus(providerToDoc(shown)).tone}>{providerStatus(providerToDoc(shown)).text}</Chip> },
            { label: "Position", value: `${selected + 1} of ${providers.length}` },
            { label: kindOf(shown.type) === "saml" ? "Metadata URL" : "Issuer", value: <code>{providerUrl(providerToDoc(shown)) || "no URL"}</code> },
            ...(kindOf(shown.type) === "oidc" ? [{ label: "Client secret", value: secretState(shown) }] : []),
          ]}
          actions={
            confirmingRemove ? (
              <>
                <span role="alert">Remove {shown.label}?</span>
                <button type="button" class="button" onClick={() => setConfirmingRemove(false)}>
                  No
                </button>
                <button type="button" class="button button-danger" onClick={() => remove(selected)}>
                  Yes, remove
                </button>
              </>
            ) : (
              <>
                <button type="button" class="button button-danger" disabled={readOnly} onClick={() => setConfirmingRemove(true)}>
                  Remove
                </button>
                <button type="button" class="button" disabled={readOnly || selected === 0} onClick={() => move(selected, -1)}>
                  Move up
                </button>
                <button type="button" class="button" disabled={readOnly || selected === providers.length - 1} onClick={() => move(selected, 1)}>
                  Move down
                </button>
                <button type="button" class="button button-primary" onClick={() => edit(selected)}>
                  Edit
                </button>
              </>
            )
          }
        />
      )}
      {editing === -1 ? (
        <ProviderForm initial={blankProvider("okta")} isNew readOnly={readOnly} onDone={(next) => (onChange([...providers, next]), setEditing(null))} onCancel={() => setEditing(null)} />
      ) : (
        <div class="row">
          <button type="button" class="button button-sm" disabled={readOnly || providers.length >= PROVIDERS_MAX} onClick={() => setEditing(-1)}>
            Add provider
          </button>
        </div>
      )}
      {error && (
        <p class="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
